/**
 * DayView
 * Single-day calendar view with 30-minute intervals.
 */
import { useMemo } from "react";
import { startOfDay, format, isToday } from "date-fns";
import { de } from "date-fns/locale";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Skeleton, TooltipProvider } from "@jourfx/ui";
import { AppointmentBlock } from "./AppointmentBlock";
import { CurrentTimeIndicator } from "./CurrentTimeIndicator";
import type { CalendarAppointment } from "./AppointmentBlock";

const START_HOUR = 8;
const END_HOUR = 18;
const SLOT_MINUTES = 30;
const TOTAL_SLOTS = ((END_HOUR - START_HOUR) * 60) / SLOT_MINUTES;

interface DayViewProps {
  currentDate: Date;
  onSlotClick?: (timestamp: number) => void;
  gutachterId?: string;
}

export function DayView({ currentDate, onSlotClick, gutachterId }: DayViewProps) {
  const dayStart = startOfDay(currentDate);
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

  const appointments = useQuery(api.appointments.index.listByDateRange, {
    startDate: dayStart.getTime(),
    endDate: dayEnd.getTime(),
    gutachterId: gutachterId || undefined,
  });

  const timeLabels = useMemo(() => {
    const labels: string[] = [];
    for (let i = 0; i <= TOTAL_SLOTS; i++) {
      const totalMinutes = START_HOUR * 60 + i * SLOT_MINUTES;
      const h = Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, "0");
      const m = (totalMinutes % 60).toString().padStart(2, "0");
      labels.push(`${h}:${m}`);
    }
    return labels;
  }, []);

  const isLoading = appointments === undefined;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-auto rounded-lg border bg-card">
        {/* Header */}
        <div
          className="grid border-b bg-muted/50"
          style={{ gridTemplateColumns: "4rem 1fr" }}
        >
          <div className="border-r px-2 py-3" />
          <div className={`px-2 py-3 text-sm ${isToday(currentDate) ? "font-bold text-primary" : "text-muted-foreground"}`}>
            {format(currentDate, "EEEE, dd.MM.yyyy", { locale: de })}
          </div>
        </div>

        {/* Grid */}
        <div className="relative grid" style={{ gridTemplateColumns: "4rem 1fr" }}>
          <div className="relative border-r">
            {timeLabels.map((label, i) => (
              <div
                key={label}
                className="h-12 border-b px-1 text-right text-[11px] leading-none text-muted-foreground"
              >
                {i < timeLabels.length - 1 ? label : ""}
              </div>
            ))}
          </div>

          <div className="relative">
            {Array.from({ length: TOTAL_SLOTS }, (_, slotIdx) => {
              const totalMinutes = START_HOUR * 60 + slotIdx * SLOT_MINUTES;
              const slotDate = new Date(currentDate);
              slotDate.setHours(
                Math.floor(totalMinutes / 60),
                totalMinutes % 60,
                0,
                0
              );
              return (
                <div
                  key={slotIdx}
                  className="h-12 border-b transition-colors hover:bg-accent/50"
                  onClick={() => onSlotClick?.(slotDate.getTime())}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onSlotClick?.(slotDate.getTime());
                    }
                  }}
                  aria-label={`${format(slotDate, "EEEE dd.MM., HH:mm", { locale: de })} Uhr`}
                />
              );
            })}

            {isLoading ? (
              <div className="absolute inset-0 flex flex-col gap-2 p-2">
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-16 w-full rounded" />
              </div>
            ) : (
              (appointments as CalendarAppointment[]).map((apt) => (
                <AppointmentBlock
                  key={apt._id}
                  appointment={apt}
                  startHour={START_HOUR}
                  endHour={END_HOUR}
                />
              ))
            )}

            {isToday(currentDate) && (
              <CurrentTimeIndicator startHour={START_HOUR} endHour={END_HOUR} />
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
